import sortBy from 'lodash/sortBy';
import isNil from 'lodash/isNil';
import findLast from 'lodash/findLast';
import findLastIndex from 'lodash/findLastIndex';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { DurationPipe } from '../../pipes/duration';
import { ServerApi } from '../../services/server-api';
import { IMoment, moment } from '../../lib/moment';
import { extractResponseBody } from '../../lib/utils';
import { formatDurationTime } from './utils';

type DispatchStatus = 'pending' | 'planned' | 'active' | 'completed' | 'cancelled';

interface ILocationCords {
  lat: number;
  lng: number;
}

interface ILastKnownLocation {
  ILocationLatLon;
  city: string;
  state: string;
}

interface IServerDispatchStop {
  dispatch_stop: {
    status: string;
    form_ids: string[];
    number: number;
    early_date: string;
    late_date: string;
    dateTimeZoneAbbr: string;
    dispatch_location: IDispatchLocation;
    arrive_time: string;
    depart_time: string;
    arrive_time_tz_abbr: string;
    depart_time_tz_abbr: string;
  };
}

interface IServerVehicleLocation {
  lat: number;
  lon: number;
  city: string;
  state: string;
  located_at: string;
}

interface IServerDispatchResponse {
  dispatch: IServerDispatch;
}

interface IServerDispatch {
  id: number;
  order_number: string;
  status: DispatchStatus;
  shipper_arrive_time: string;
  shipper_arrive_time_tz_abbr: string;
  shipper_depart_time: string;
  shipper_depart_time_tz_abbr: string;
  consignee_arrive_time: string;
  consignee_arrive_time_tz_abbr: string;
  consignee_depart_time: string;
  consignee_depart_time_tz_abbr: string;
  product: string;
  pickup_early_date: string;
  pickup_late_date: string;
  pickup_early_date_tz_abbr: string;
  delivery_early_date: string;
  delivery_late_date: string;
  delivery_early_date_tz_abbr: string;
  order_pieces: string;
  order_weight: string;
  order_volume: string;
  consignee_comments: string;
  company: {
    name: string;
  };
  company_logo: string;
  consignee_dispatch_location: IDispatchLocation;
  shipper_dispatch_location: IDispatchLocation;
  dispatch_stops: IServerDispatchStop[];
  vehicle_locations: IServerVehicleLocation[];
  skip_shipper_consignee_stops?: boolean;
}

interface IDispatchLocation {
  id: number;
  lat: number;
  lon: number;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface IDispatchStop {
  number: number;
  isCurrentLocation: boolean;
  isLastKnownLocation: boolean;
  stopType: string;
  status: 'arrivalPending' | 'arrived' | 'departed' | 'in_progress' | 'available';
  arrivalDate: string;
  arrivalTZ?: string;
  arrivalDateFormatted: string;
  departureDate: string;
  departureTZ?: string;
  departureDateFormatted: string;
  expectedStartDate: IMoment;
  expectedEndDate: IMoment;
  dateTimeZoneAbbr: string;
  scheduleDateTimeRange: string;
  isArrivalDelayed: boolean;
  lateArrivalTime: string;
  dispatchLocation: IDispatchLocation;
  lastKnownLocationTrackTime?: string;
  isNextStop?: boolean;
  skipShipperConsigneeStops?: boolean;
}

interface ICreateDispatchStopParams {
  dispatchLocation: IDispatchLocation;
  stopType: string;
  dispatchNumber: number;
  expectedStartDate: IMoment;
  expectedEndDate: IMoment;
  dateTimeZoneAbbr: string;
  arrivalDate: string;
  arrivalTZ: string;
  departureDate: string;
  departureTZ: string;
  skipShipperConsigneeStops?: boolean;
  status?: 'arrivalPending' | 'arrived' | 'departed' | 'in_progress' | 'available';
}

export interface IDispatchOrderDetails {
  product: string;
  pieces: string;
  weight: string;
  volume: string;
  consigneeComments: string;
}

interface IDispatchSummary {
  summary: string;
  isDelayed: boolean;
}

export interface IDispatch {
  id: number;
  orderNumber: string;
  status: DispatchStatus;
  isDispatchStarted: boolean;
  numberOfStops: number;
  dispatchSummary: IDispatchSummary;
  company: {
    name: string;
    logo: string;
    companyLogoExists: boolean;
  };
  dispatchOrderDetails?: IDispatchOrderDetails;
  shipperDispatchLocation: IDispatchLocation;
  consigneeDispatchLocation: IDispatchLocation;
  dispatchStops: IDispatchStop[];
  vehicleLocations: ILocationCords[];
  lastKnownLocation?: ILastKnownLocation;
  dispatchFrom?: string;
  dispatchTo?: string;
  skipShipperConsigneeStops?: boolean;
}

const DATE_TIME_FORMAT: string = 'MMM DD, h:mm A';
const TIME_FORMAT: string = 'h:mm A';

@Injectable()
export class DispatchTrackingService {
  constructor(private api: ServerApi, private durationPipe: DurationPipe) { }

  public isDispatchDeparted(dispatchStop: IDispatchStop): boolean {
    if (dispatchStop.skipShipperConsigneeStops) return dispatchStop.status === 'departed';
    return (!dispatchStop.isCurrentLocation &&
      (dispatchStop.stopType === 'pickup' || dispatchStop.stopType === 'pickupOrDropoff') ) ||
      dispatchStop.status === 'departed' && dispatchStop.stopType !== 'dropoff';
  }

  public getDispatchStopStatus(arrivalDate: string, departureDate: string): IDispatchStop['status'] {
    if (departureDate) {
      return 'departed';
    }
    if (arrivalDate) {
      return 'arrived';
    } else return 'arrivalPending';
  }

  public getDispatchDetails(token: string, iv: string): Observable<IDispatch> {
    const params = new Map();
    params.set('token', decodeURIComponent(token));
    params.set('iv', decodeURIComponent(iv));
    return this.api.get(
      `dispatch_share`,
      params,
      's1',
      this.setPublicHeader())
      .pipe(
        map(extractResponseBody),
        map((dispatch: IServerDispatchResponse) => dispatch.dispatch),
        map(this.transformData.bind(this)),
      );
  }

  public reshareDispatchURL(email: string): Observable<any> {
    const params = new Map();
    params.set('email_addresses', [email]);
    params.set('tracking_url', window.location.href);
    return this.api.publicPost(
      `dispatch_share/reshare`,
      params,
      's1',
      this.setPublicHeader())
      .pipe(
        map(extractResponseBody),
      );
  }

  private setPublicHeader(): Map<string, string> {
    const headers = new Map();
    headers.set('X-Web-Share-Api-Key', WEB_SHARE_API_KEY);
    return headers;
  }

  private transformData(serverDispatch: IServerDispatch): IDispatch {
    const sortedVehicleLocations: IServerVehicleLocation[] = serverDispatch.vehicle_locations
      .sort((aLocation: IServerVehicleLocation, bLocation: IServerVehicleLocation) => {
        return (moment(aLocation.located_at) - moment(bLocation.located_at));
      });
    const lastKnownLocation: IServerVehicleLocation = sortedVehicleLocations.length > 0 ?
      sortedVehicleLocations[sortedVehicleLocations.length - 1] : null
    ;

    let dispatchStops: IDispatchStop[] = this.transformDispatchStop(serverDispatch);
    this.setCurrentLocation(dispatchStops);
    this.calculateLateTime(dispatchStops);
    this.calculateIfNextStopArrivalIsDelayed(dispatchStops, lastKnownLocation);

    if (!isNil(lastKnownLocation)) {
      const lastKnownLocationDispatchStop: IDispatchStop = this.transformLastLocation(lastKnownLocation);
      dispatchStops = this.addLastKnowStopInDispatchStops(lastKnownLocationDispatchStop, dispatchStops);
    }

    const dispatch: IDispatch = {
      id: serverDispatch.id,
      orderNumber: serverDispatch.order_number,
      status: serverDispatch.status,
      isDispatchStarted: !!(serverDispatch.shipper_arrive_time) || !!serverDispatch.dispatch_stops[0]?.dispatch_stop.arrive_time,
      numberOfStops: serverDispatch.dispatch_stops.length,
      dispatchSummary: this.getDispatchSummary(dispatchStops, serverDispatch.status),
      company: {
        name: serverDispatch.company.name,
        logo: serverDispatch.company_logo ? serverDispatch.company_logo : '/logo.svg',
        companyLogoExists: serverDispatch.company_logo ? true : false,
      },
      shipperDispatchLocation: serverDispatch.shipper_dispatch_location,
      consigneeDispatchLocation: serverDispatch.consignee_dispatch_location,
      dispatchStops,
      skipShipperConsigneeStops: serverDispatch.skip_shipper_consignee_stops,
      vehicleLocations: sortedVehicleLocations
        .map((location: IServerVehicleLocation) => ({
          lat: location.lat,
          lng: location.lon,
        })),
    };
    dispatch.dispatchFrom = dispatch.shipperDispatchLocation && !dispatch.skipShipperConsigneeStops ?
      `${dispatch.shipperDispatchLocation.city}, ${dispatch.shipperDispatchLocation.state}` :
      `${dispatchStops[0].dispatchLocation.city}, ${dispatchStops[0].dispatchLocation.state}`;
    dispatch.dispatchTo = dispatch.consigneeDispatchLocation && !dispatch.skipShipperConsigneeStops ?
      `${dispatch.consigneeDispatchLocation.city}, ${dispatch.consigneeDispatchLocation.state}` :
      `${dispatchStops[dispatchStops.length - 1].dispatchLocation.city}, ${dispatchStops[dispatchStops.length - 1].dispatchLocation.state}`;
    dispatch.dispatchOrderDetails = {
      product: serverDispatch.product,
      pieces: serverDispatch.order_pieces,
      weight: serverDispatch.order_weight,
      volume: serverDispatch.order_volume,
      consigneeComments: serverDispatch.consignee_comments,
    };
    if (!dispatch.isDispatchStarted) { dispatch.dispatchStops[0].isNextStop = true; }
    return dispatch;
  }

  private getDispatchSummary(dispatchStops: IDispatchStop[], dispatchStatus: DispatchStatus): IDispatchSummary {
    const currentDispatchIndex: number = dispatchStops.findIndex(stop => stop.isCurrentLocation);
    const currentDispatchStop: IDispatchStop = currentDispatchIndex > -1 ? dispatchStops[currentDispatchIndex] : null;
    if (currentDispatchStop) {
      if (currentDispatchStop.stopType !== 'dropoff' && currentDispatchStop.status === 'arrived') {
        return this.getSummaryForArrivedStops(currentDispatchStop, dispatchStatus);
      } else if (currentDispatchStop.stopType !== 'dropoff' && currentDispatchStop.status === 'departed') {
        const nextDispatchStop = dispatchStops[currentDispatchIndex + 1]?.isLastKnownLocation ?
          dispatchStops[currentDispatchIndex + 2] : dispatchStops[currentDispatchIndex + 1];
        if (nextDispatchStop?.isArrivalDelayed) {
          return this.getSummaryForArrivedStops(nextDispatchStop, dispatchStatus);
        } else {
          return this.getSummaryForScheduledStop(nextDispatchStop);
        }
      } else if (currentDispatchStop.stopType === 'dropoff') {
        return this.getSummaryForArrivedStops(currentDispatchStop, dispatchStatus);
      }
    } else {
      return this.getSummaryForScheduledStop(dispatchStops[0]);
    }
  }

  private createDispatchStop(params: ICreateDispatchStopParams): IDispatchStop {
    const {
      dispatchLocation,
      stopType,
      dispatchNumber,
      expectedStartDate,
      expectedEndDate,
      dateTimeZoneAbbr,
      arrivalDate,
      arrivalTZ,
      departureDate,
      departureTZ,
      status,
      skipShipperConsigneeStops,
    } = params;

    const dispatchStatus: IDispatchStop['status'] = skipShipperConsigneeStops ?
      status : this.getDispatchStopStatus(arrivalDate, departureDate);

    return {
      isCurrentLocation: false,
      isLastKnownLocation: false,
      arrivalDate,
      arrivalTZ,
      departureDate,
      departureTZ,
      stopType,
      status: dispatchStatus,
      dispatchLocation: this.transformDispatchLocationObj(dispatchLocation),
      number: dispatchNumber,
      expectedStartDate,
      dateTimeZoneAbbr,
      expectedEndDate,
      scheduleDateTimeRange: this.getTimeDateRangeString(expectedStartDate, expectedEndDate),
      arrivalDateFormatted: dispatchStatus !== 'arrivalPending' ? moment.parseZone(arrivalDate).format(DATE_TIME_FORMAT) : '',
      departureDateFormatted: dispatchStatus !== 'arrivalPending' && departureDate ?
        moment.parseZone(departureDate).format(DATE_TIME_FORMAT) : '',
      isArrivalDelayed: false,
      lateArrivalTime: '',
      skipShipperConsigneeStops,
    };
  }

  private transformDispatchStop(dispatch: IServerDispatch): IDispatchStop[] {
    const dispatchStops: any = dispatch.dispatch_stops
    .map(m => m.dispatch_stop);
    const sortedDispatches = sortBy(dispatchStops, a => a.number);
    let dispatchPickUpOrDropoffStops: IDispatchStop[];
    if (dispatch.skip_shipper_consignee_stops) {
      dispatchPickUpOrDropoffStops = sortedDispatches.map(
        (dispatchStop: any, index: number) => (
          this.createDispatchStop({
            dispatchLocation: dispatchStop.dispatch_location,
            stopType: dispatchStop.stop_type,
            status: dispatchStop.status,
            dispatchNumber: index + 1,
            expectedStartDate: !isNil(dispatchStop.early_date) ? moment.parseZone(dispatchStop.early_date) : null,
            expectedEndDate: !isNil(dispatchStop.late_date) ? moment.parseZone(dispatchStop.late_date) : null,
            dateTimeZoneAbbr: dispatchStop.early_date_tz_abbr || dispatchStop.late_date_tz_abbr || '',
            arrivalDate: !isNil(dispatchStop.arrive_time) ? moment.parseZone(dispatchStop.arrive_time) : null,
            arrivalTZ: dispatchStop.arrive_time_tz_abbr,
            departureDate: !isNil(dispatchStop.depart_time) ? moment.parseZone(dispatchStop.depart_time) : null,
            departureTZ: dispatchStop.depart_time_tz_abbr,
            skipShipperConsigneeStops: dispatch.skip_shipper_consignee_stops,
          })
        ),
      );
      return dispatchPickUpOrDropoffStops;
    }
    const dispatchPickupStop: IDispatchStop = this.createDispatchStop({
      dispatchLocation: dispatch.shipper_dispatch_location,
      stopType: 'pickup',
      dispatchNumber: 1,
      expectedStartDate: !isNil(dispatch.pickup_early_date) ? moment.parseZone(dispatch.pickup_early_date) : null,
      expectedEndDate: !isNil(dispatch.pickup_late_date) ? moment.parseZone(dispatch.pickup_late_date) : null,
      dateTimeZoneAbbr: dispatch.pickup_early_date_tz_abbr ? dispatch.pickup_early_date_tz_abbr : '',
      arrivalDate: !isNil(dispatch.shipper_arrive_time) ? moment.parseZone(dispatch.shipper_arrive_time) : null,
      arrivalTZ: dispatch.shipper_arrive_time_tz_abbr,
      departureDate: !isNil(dispatch.shipper_depart_time) ? moment.parseZone(dispatch.shipper_depart_time) : null,
      departureTZ: dispatch.shipper_depart_time_tz_abbr,
    });
    const dispatchDropOffStop: IDispatchStop = this.createDispatchStop({
      dispatchLocation: dispatch.consignee_dispatch_location,
      stopType: 'dropoff',
      dispatchNumber: dispatch.dispatch_stops.length + 2,
      expectedStartDate: !isNil(dispatch.delivery_early_date) ? moment.parseZone(dispatch.delivery_early_date) : null,
      expectedEndDate: !isNil(dispatch.delivery_late_date) ? moment.parseZone(dispatch.delivery_late_date) : null,
      dateTimeZoneAbbr: dispatch.delivery_early_date_tz_abbr ? dispatch.delivery_early_date_tz_abbr : '',
      arrivalDate: !isNil(dispatch.consignee_arrive_time) ? moment.parseZone(dispatch.consignee_arrive_time) : null,
      arrivalTZ: dispatch.consignee_arrive_time_tz_abbr,
      departureDate: !isNil(dispatch.consignee_depart_time) ? moment.parseZone(dispatch.consignee_depart_time) : null,
      departureTZ: dispatch.consignee_depart_time_tz_abbr,
    });

    dispatchPickUpOrDropoffStops = sortedDispatches.map(
      (dispatchStop: any, index: number) => (
        this.createDispatchStop({
          dispatchLocation: dispatchStop.dispatch_location,
          stopType: 'pickupOrDropoff',
          dispatchNumber: index + 2, // adding plus two because dispatch pickup stop is not included in the disptach stops
          // and index start from zero
          expectedStartDate: !isNil(dispatchStop.early_date) ? moment.parseZone(dispatchStop.early_date) : null,
          expectedEndDate: !isNil(dispatchStop.late_date) ? moment.parseZone(dispatchStop.late_date) : null,
          dateTimeZoneAbbr: dispatchStop.early_date_tz_abbr || dispatchStop.late_date_tz_abbr || '',
          arrivalDate: !isNil(dispatchStop.arrive_time) ? moment.parseZone(dispatchStop.arrive_time) : null,
          arrivalTZ: dispatchStop.arrive_time_tz_abbr,
          departureDate: !isNil(dispatchStop.depart_time) ? moment.parseZone(dispatchStop.depart_time) : null,
          departureTZ: dispatchStop.depart_time_tz_abbr,
        })
      ),
    );
    dispatchPickUpOrDropoffStops.unshift(dispatchPickupStop);
    dispatchPickUpOrDropoffStops.push(dispatchDropOffStop);
    return dispatchPickUpOrDropoffStops;
  }

  private setCurrentLocation(dispatchStops: IDispatchStop[]): void {
    const latestStopWithArrivalOnly: IDispatchStop = dispatchStops.
      find(stop => stop.status === 'arrived');

    if (latestStopWithArrivalOnly) latestStopWithArrivalOnly.isCurrentLocation = true;
    else {
      const lastStopWithArrivalAndDispatch: IDispatchStop = findLast(dispatchStops, stop => {
        return !isNil(stop.arrivalDate) && !isNil(stop.departureDate);
      });
      if (lastStopWithArrivalAndDispatch) lastStopWithArrivalAndDispatch.isCurrentLocation = true;
    }
  }

  private calculateLateTime(dispatchStops: IDispatchStop[]): void {
    dispatchStops.forEach(dispatchStop => {
      const momentArrivalDate: IMoment = moment.parseZone(dispatchStop.arrivalDate).utc();
      let dispatchEndDateUTCMoment: Moment = momentArrivalDate;
      if (dispatchStop.expectedEndDate) dispatchEndDateUTCMoment = dispatchStop.expectedEndDate.utc();
      if (dispatchStop.stopType !== 'lastKnownLocation' &&
        momentArrivalDate.isAfter(dispatchEndDateUTCMoment)) {
        const lateArrivalTime = momentArrivalDate.diff(dispatchEndDateUTCMoment, 'second');
        dispatchStop.lateArrivalTime = lateArrivalTime.toString();
      }
    });
  }

  private calculateIfNextStopArrivalIsDelayed(dispatchStops: IDispatchStop[], lastKnownStop: IServerVehicleLocation) {
    const latestArrivedStopIndex: number = findLastIndex(dispatchStops, stop => stop.status === 'arrived');
    let nextStop: IDispatchStop = null;
    if (latestArrivedStopIndex > -1) {
      nextStop = dispatchStops[latestArrivedStopIndex + 1];
      const latestStopWithArrivalOnly: IDispatchStop = dispatchStops[latestArrivedStopIndex];
      if (!isNil(lastKnownStop) && latestStopWithArrivalOnly.stopType !== 'dropoff') {
        const lastKnownLocationUTC: IMoment = moment(lastKnownStop.located_at).utc();
        const dateToCheck = nextStop.expectedEndDate ? nextStop.expectedEndDate.utc() : lastKnownLocationUTC;
        if (lastKnownLocationUTC.isAfter(dateToCheck)) {
          nextStop.isArrivalDelayed = true;
        }
      }
    } else if (findLastIndex(dispatchStops, stop => stop.status === 'departed') > -1) {
      const latestDepartedStopIndex: number = findLastIndex(dispatchStops, stop => stop.status === 'departed');
      const latestDepartedStop: IDispatchStop = dispatchStops[latestDepartedStopIndex];
      nextStop = dispatchStops[latestDepartedStopIndex + 1];
      if (!isNil(lastKnownStop) && latestDepartedStop.stopType !== 'dropoff') {
        const lastKnownLocationUTC: IMoment = moment(lastKnownStop.located_at).utc();
        const dateToCheck = nextStop.expectedEndDate ? nextStop.expectedEndDate.utc() : lastKnownLocationUTC;
        if (lastKnownLocationUTC.isAfter(dateToCheck)) {
          nextStop.isArrivalDelayed = true;
        }
      }
    } else {
      if (!isNil(lastKnownStop)) {
        const currentUTCMoment: IMoment = moment().utc();
        const pickupStop: IDispatchStop = dispatchStops[0]; // first stop will always be the Pickup Stop
        const dateToCheck = nextStop && nextStop.expectedEndDate ? nextStop.expectedEndDate.utc() : currentUTCMoment;
        if (!isNil(pickupStop) && currentUTCMoment.isAfter(dateToCheck)) {
          pickupStop.isArrivalDelayed = true;
        }
      }
    }
    if (nextStop) nextStop.isNextStop = true;
  }

  private transformDispatchLocationObj(location: IDispatchLocation): IDispatchLocation {
    return {
      id: location.id,
      lat: location.lat,
      lon: location.lon,
      name: location.name,
      address1: location.address1,
      address2: location.address2,
      city: location.city,
      state: location.state,
      zip: location.zip,
      country: location.country,
    };
  }

  private getSummaryForScheduledStop(dispatchStop: IDispatchStop): IDispatchSummary {
    if (isNil(dispatchStop?.expectedStartDate) && isNil(dispatchStop?.expectedEndDate)) {
      return {
        summary: '',
        isDelayed: false,
      };
    }
    const locationName = dispatchStop.dispatchLocation.name;
    const dateTimeString = this.getTimeDateRangeString(dispatchStop?.expectedStartDate, dispatchStop?.expectedEndDate, true);
    return {
      summary: $localize`Scheduled to arrive at ${locationName} ${dateTimeString} ${dispatchStop.dateTimeZoneAbbr}.`,
      isDelayed: false,
    };
  }

  private getSummaryForArrivedStops(dispatchStop: IDispatchStop, dispatchStatus: DispatchStatus): IDispatchSummary {
    const locationName = dispatchStop?.dispatchLocation.name;
    const dispatchArrivalMoment: IMoment = moment.parseZone(dispatchStop?.arrivalDate);
    if (dispatchStop?.isArrivalDelayed) {
      return {
        summary: $localize`Delayed arrival at ${locationName}.`,
        isDelayed: true,
      };
    } else {
      let dateTimeString: string = '';
      const arrivalTimeDiff: number = moment().utc().diff(dispatchArrivalMoment.utc(), 'hours');
      if (arrivalTimeDiff >= 12 || dispatchStatus === 'completed') {
        dateTimeString = moment.parseZone(dispatchArrivalMoment).format('MMM DD, h:mm A');
        const dispatchStopArrivalTZ = dispatchStop?.arrivalTZ;
        const summary = dispatchStatus === 'completed' ?
          $localize`Arrived at ${locationName} on <br/> ${dateTimeString} ${dispatchStopArrivalTZ}.` :
          $localize`Arrived at ${locationName} on ${dateTimeString} ${dispatchStopArrivalTZ}.`;
        return {
          summary,
          isDelayed: false,
        };
      } else {
        dateTimeString = this.getTimeDurationDiff(dispatchArrivalMoment);
        return {
          summary: $localize`Arrived at ${locationName} ${dateTimeString} ago.`,
          isDelayed: false,
        };
      }
    }
  }

  private getTimeDateRangeString(expectedStartDate: IMoment, expectedEndDate: IMoment, addPrefix: boolean = false): string {
    expectedStartDate = moment.parseZone(expectedStartDate);
    expectedEndDate = moment.parseZone(expectedEndDate);

    if (!expectedStartDate.isValid() && !expectedEndDate.isValid()) return '';

    const isSameDay: boolean = expectedStartDate.isSame(expectedEndDate, 'day');
    const isToday: boolean = moment().utc().isSame(expectedEndDate, 'day');
    const formattedTimeRange: string = this.formattedTimeRangeStringWindow(expectedStartDate, expectedEndDate, TIME_FORMAT);
    const formattedExpectedStartDate: string = expectedStartDate.format('MMM DD,');

    if (isSameDay) {
      if (isToday) {
        return addPrefix ? `today between ${formattedTimeRange}` : `${formattedExpectedStartDate} ${formattedTimeRange}`;
      } else {
        return addPrefix
          ? `on ${formattedExpectedStartDate} between ${formattedTimeRange}`
          : `${formattedExpectedStartDate} ${formattedTimeRange}`
        ;
      }
    }

    const formattedDateTimeRange: string =
      this.formattedTimeRangeStringWindow(expectedStartDate, expectedEndDate, DATE_TIME_FORMAT)
    ;

    return addPrefix ? `between ${formattedDateTimeRange}` : formattedDateTimeRange;
  }

  private formattedTimeRangeStringWindow(startDate: IMoment, endDate: IMoment, format: string) {
    if (!startDate.isValid()) return endDate.format(format);
    if (!endDate.isValid()) return startDate.format(format);

    return `${startDate.format(format)} - ${endDate.format(format)}`;
  }

  private transformLastLocation(lastKnownLocation: IServerVehicleLocation): IDispatchStop {
    const lastKnownLocationTrackTime: string = this.getTimeDurationDiff(moment.parseZone(lastKnownLocation.located_at), true);
    return {
      number: 0,
      isCurrentLocation: false,
      isLastKnownLocation: true,
      lateArrivalTime: '',
      lastKnownLocationTrackTime,
      stopType: 'lastKnownLocation',
      status: 'arrived',
      arrivalDate: null,
      arrivalDateFormatted: null,
      departureDate: null,
      departureDateFormatted: null,
      expectedStartDate: null,
      expectedEndDate: null,
      dateTimeZoneAbbr: null,
      scheduleDateTimeRange: null,
      isArrivalDelayed: null,
      dispatchLocation: {
        id: null,
        lat: lastKnownLocation.lat,
        lon: lastKnownLocation.lon,
        name: '',
        address1: null,
        address2: null,
        city: lastKnownLocation.city,
        state: lastKnownLocation.state,
        zip: null,
        country: null,
      },
    };
  }

  private addLastKnowStopInDispatchStops(lastKnownStop: IDispatchStop, dispatchStops: IDispatchStop[]) {
    const currentStopIndex: number = dispatchStops.findIndex(stop => stop.isCurrentLocation);
    if (currentStopIndex > -1) {
      const currentStop: IDispatchStop = dispatchStops[currentStopIndex];
      if (currentStop.stopType !== 'dropoff' && currentStop.status === 'departed') {
        dispatchStops.splice(currentStopIndex + 1, 0, lastKnownStop);
      }
    }
    return dispatchStops;
  }

  private getTimeDurationDiff(momentToCompare: IMoment, hideLessThanTwoMinutes: boolean = false): string {
    const timeDiffInSeconds: number = moment().utc().diff(momentToCompare.utc(), 'second');
    const showSeconds = false;
    const showDays = true;
    const roundOff = false;
    const useShortHandUnit = true;

    const duration: string = this.durationPipe.transform(
      timeDiffInSeconds,
      showSeconds,
      showDays,
      false,
      false,
      roundOff,
      useShortHandUnit,
    );

    return (hideLessThanTwoMinutes && timeDiffInSeconds < 120) ? // less than two minutes
      $localize`Live` : formatDurationTime(duration);
  }
}
